import type React from 'react';

export type SQIconName =
  | 'people'
  | 'timer'
  | 'chart'
  | 'trophy'
  | 'check'
  | 'cross'
  | 'crown'
  | 'send'
  | 'medal'
  | 'door'
  | 'wifi-off';

export interface SQIconProps {
  name: SQIconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
  'aria-label'?: string;
}

function renderPaths(name: SQIconName): React.ReactNode {
  switch (name) {
    case 'people':
      return (
        <>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </>
      );
    case 'timer':
      return (
        <>
          <circle cx="12" cy="14" r="7" />
          <polyline points="12 10 12 14 15 14" />
          <line x1="10" y1="3" x2="14" y2="3" />
          <line x1="12" y1="3" x2="12" y2="7" />
        </>
      );
    case 'chart':
      return (
        <>
          <rect x="3" y="13" width="5" height="8" rx="1" fill="currentColor" stroke="none" />
          <rect x="10" y="8" width="5" height="13" rx="1" fill="currentColor" stroke="none" />
          <rect x="17" y="3" width="4" height="18" rx="1" fill="currentColor" stroke="none" />
        </>
      );
    case 'trophy':
      return (
        <>
          <path d="M7 3h10v9a5 5 0 0 1-10 0V3z" />
          <path d="M7 8H3V6h4" />
          <path d="M17 8h4V6h-4" />
          <line x1="12" y1="17" x2="12" y2="21" />
          <line x1="9" y1="21" x2="15" y2="21" />
        </>
      );
    case 'check':
      return <polyline points="20 6 9 17 4 12" />;
    case 'cross':
      return (
        <>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </>
      );
    case 'crown':
      return (
        <>
          <polyline points="2 20 5 8 10 14 12 4 14 14 19 8 22 20" />
          <line x1="2" y1="20" x2="22" y2="20" />
        </>
      );
    case 'send':
      return (
        <>
          <line x1="22" y1="2" x2="11" y2="13" />
          <polygon points="22 2 15 22 11 13 2 9 22 2" />
        </>
      );
    case 'medal':
      return (
        <>
          <circle cx="12" cy="15" r="5" />
          <path d="M9 3h6l-1 7h-4L9 3z" />
        </>
      );
    case 'door':
      return (
        <>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </>
      );
    case 'wifi-off':
      return (
        <>
          <line x1="1" y1="1" x2="23" y2="23" />
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
          <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <circle cx="12" cy="20" r="1" fill="currentColor" stroke="none" />
        </>
      );
  }
}

export function SQIcon({
  name,
  size = 24,
  color = 'currentColor',
  strokeWidth = 2,
  'aria-label': ariaLabel,
}: SQIconProps) {
  const decorative = ariaLabel === undefined;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ color }}
      aria-hidden={decorative ? true : undefined}
      aria-label={ariaLabel}
      role={decorative ? undefined : 'img'}
    >
      {renderPaths(name)}
    </svg>
  );
}
